import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRightIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Spinner } from '../common';
import type { ServiceLocationInfo } from '../../types/firestore';
import { getSmartSearchSuggestions, type SmartSearchSelection } from '../../utils/smartSearch';

export interface SearchBarProps {
  onSearch?: (item: SmartSearchSelection | null) => void;
  disabled?: boolean;
  value?: string;
  selectedSuggestion?: SmartSearchSelection | null;
  services?: ServiceLocationInfo[];
  placeholder?: string;
}

const suggestionTypeLabels: Record<SmartSearchSelection['type'], string> = {
  issue: 'Problem',
  service: 'Usługa',
  diagnostic: 'Diagnostyka',
  object: 'Sprzęt',
  category: 'Kategoria',
  provider: 'Specjalista',
  reportMissing: 'Brakująca usługa',
};

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  disabled = false,
  value = '',
  selectedSuggestion = null,
  services = [],
  placeholder = 'Np. pralka nie wiruje, diagnostyka auta, instalacja Windowsa',
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [overlayRect, setOverlayRect] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const suggestions = useMemo(
    () => getSmartSearchSuggestions(inputValue, services),
    [inputValue, services]
  );
  const showInstruction = isOpen && inputValue.trim().length === 0;
  const showSuggestions = isOpen && inputValue.trim().length > 0;
  const hasTextValue = inputValue.trim().length > 0;
  const showPanel = showInstruction || showSuggestions;

  const updateOverlayRect = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    setOverlayRect({
      left: Math.max(8, rect.left),
      top: rect.bottom + 6,
      width: Math.min(rect.width, window.innerWidth - Math.max(16, rect.left) - 8),
    });
  }, []);

  useLayoutEffect(() => {
    if (!showPanel) return undefined;
    updateOverlayRect();

    window.addEventListener('resize', updateOverlayRect);
    window.addEventListener('scroll', updateOverlayRect, true);
    return () => {
      window.removeEventListener('resize', updateOverlayRect);
      window.removeEventListener('scroll', updateOverlayRect, true);
    };
  }, [showPanel, updateOverlayRect]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const selectSuggestion = (suggestion: SmartSearchSelection) => {
    if (disabled) return;
    setInputValue(suggestion.inputSelectedLabel);
    setIsOpen(false);
    onSearch?.(suggestion);
    requestAnimationFrame(() => {
      inputRef.current?.blur();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    });
  };

  const clearSelection = () => {
    if (disabled) return;
    setInputValue('');
    setIsOpen(false);
    onSearch?.(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && suggestions.length > 0 && inputValue.trim()) {
      event.preventDefault();
      selectSuggestion(suggestions[0]);
    }
  };

  return (
    <div ref={rootRef} className="relative z-[1200] min-w-0 overflow-visible">
      <div className="relative">
        <input
          ref={inputRef}
          id="search-bar-input"
          type="text"
          inputMode="search"
          value={inputValue}
          disabled={disabled}
          autoComplete="off"
          aria-label="W czym potrzebujesz pomocy?"
          aria-controls="smart-search-suggestions"
          placeholder={placeholder}
          onFocus={() => setIsOpen(true)}
          onChange={(event) => {
            setInputValue(event.target.value);
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className={`block h-[42px] w-full min-w-0 truncate rounded-lg border border-primary-dark-300/24 bg-primary-dark-100 py-2 pl-3 pr-12 text-sm font-medium text-primary-light-100 shadow-none outline-none transition-colors duration-150 placeholder:text-primary-light-300/80 focus:border-ratuj-300/70 focus:ring-2 focus:ring-ratuj-300/60 md:h-[44px] ${
            disabled ? 'cursor-not-allowed opacity-70' : 'hover:border-primary-dark-400/45'
          }`}
        />

        <div className="absolute inset-y-0 right-1.5 flex items-center">
          {disabled ? (
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-primary-light-400">
              <Spinner size="sm" color="primary" />
            </span>
          ) : selectedSuggestion || hasTextValue ? (
            <button
              type="button"
              className="focus-outline inline-flex h-9 w-9 items-center justify-center rounded-full text-primary-light-400 transition-colors hover:bg-primary-dark-300/45 hover:text-primary-light-100"
              onClick={clearSelection}
              aria-label="Wyczyść wyszukiwanie"
              title="Wyczyść wyszukiwanie"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          ) : (
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ratuj-300">
              <MagnifyingGlassIcon className="h-4 w-4" />
            </span>
          )}
        </div>
      </div>

      {showPanel &&
        overlayRect &&
        createPortal(
          <div
            ref={panelRef}
            id="smart-search-suggestions"
            role="listbox"
            className="fixed z-[1600] max-h-[min(22rem,calc(100dvh-9rem))] overflow-y-auto overflow-x-hidden rounded-lg border border-primary-dark-400/80 bg-primary-dark-100 p-2 shadow-dropdown"
            style={{
              left: overlayRect.left,
              top: overlayRect.top,
              width: overlayRect.width,
            }}
          >
            {showInstruction && (
              <div className="space-y-2 px-2 py-2.5">
                <p className="text-sm font-semibold text-primary-light-100">
                  Napisz po swojemu, wybierz nasze dopasowanie.
                </p>
                <p className="text-xs leading-5 text-primary-light-200">
                  Jeśli nie ma idealnego wyniku, wybierz najbliższy sprzęt lub kategorię.
                </p>
                <div className="rounded-md border border-primary-dark-400/70 bg-primary-dark-200 px-2.5 py-2 text-xs leading-5 text-primary-light-200">
                  „coś buczy przy wirowaniu” → „Pralka głośno pracuje”
                </div>
              </div>
            )}

            {showSuggestions && (
              <div className="space-y-1">
                {suggestions.map((suggestion) => (
                  <button
                    key={`${suggestion.matchType}:${suggestion.id}`}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="focus-outline group flex w-full min-w-0 items-center gap-3 rounded-md px-2.5 py-2.5 text-left transition-colors duration-150 hover:bg-primary-dark-300/70 focus-visible:bg-primary-dark-300/70 active:bg-primary-dark-400/80"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => selectSuggestion(suggestion)}
                  >
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-semibold text-primary-light-100">
                        {suggestion.suggestionTitle}
                      </span>
                      <span className="mt-0.5 truncate text-xs text-primary-light-400">
                        {suggestion.suggestionMeta}
                      </span>
                      {suggestion.description && (
                        <span className="mt-1 line-clamp-2 text-xs leading-5 text-primary-light-200">
                          {suggestion.description}
                        </span>
                      )}
                    </span>
                    <span className="hidden shrink-0 text-[10px] font-semibold uppercase tracking-wide text-primary-light-400 sm:inline">
                      {suggestionTypeLabels[suggestion.type]}
                    </span>
                    <ArrowRightIcon className="h-4 w-4 shrink-0 text-primary-light-300 transition-colors group-hover:text-ratuj-300" />
                  </button>
                ))}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default SearchBar;
